import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import type {
    ConversationRowRef,
    ConversationTriage,
    ConversationTriagePatch,
    ConversationsPageResponse
} from '../services/conversations';
import { CONVERSATIONS_INFINITE_KEY, CONVERSATIONS_PAGE_KEY } from './queryKeys.ts';

export type ConversationTriageSnapshot = ReadonlyArray<readonly [QueryKey, unknown]>;

// Both list modes; home and the deep-link detail catch up on the settle refetch.
export const CONVERSATION_TRIAGE_LIST_KEYS: readonly QueryKey[] = [
    CONVERSATIONS_PAGE_KEY,
    [CONVERSATIONS_INFINITE_KEY]
];

// A written field is the staff's own from then on, so its AI marker goes.
export function mergeConversationTriage<T extends ConversationTriage>(
    triage: T,
    patch: ConversationTriagePatch
): T {
    return {
        ...triage,
        priority: patch.priority ?? triage.priority,
        frustration: patch.frustration ?? triage.frustration,
        priorityByAi: patch.priority === undefined ? triage.priorityByAi : false,
        frustrationByAi: patch.frustration === undefined ? triage.frustrationByAi : false
    };
}

// Each thread has its own triage, so the same patient's other row is left alone.
function patchPage(
    page: ConversationsPageResponse,
    target: ConversationRowRef,
    patch: ConversationTriagePatch
): ConversationsPageResponse {
    return {
        ...page,
        data: page.data.map((row) =>
            row.patientId === target.patientId && row.status === target.status
                ? mergeConversationTriage(row, patch)
                : row
        )
    };
}

export function snapshotConversationTriage(queryClient: QueryClient): ConversationTriageSnapshot {
    return CONVERSATION_TRIAGE_LIST_KEYS.flatMap((queryKey) =>
        queryClient.getQueriesData({ queryKey })
    );
}

export function restoreConversationTriage(
    queryClient: QueryClient,
    snapshot: ConversationTriageSnapshot
): void {
    snapshot.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
}

export function applyOptimisticConversationTriage(
    queryClient: QueryClient,
    target: ConversationRowRef,
    patch: ConversationTriagePatch
): void {
    const [pagedKey, infiniteKey] = CONVERSATION_TRIAGE_LIST_KEYS;
    queryClient.setQueriesData<ConversationsPageResponse>(
        { queryKey: pagedKey },
        (page) => page && patchPage(page, target, patch)
    );
    queryClient.setQueriesData<InfiniteData<ConversationsPageResponse>>(
        { queryKey: infiniteKey },
        (data) => data && { ...data, pages: data.pages.map((p) => patchPage(p, target, patch)) }
    );
}
